import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Alert, Badge, Button, Card, cn } from './ui';
import { api } from '../lib/api';

interface Credentials {
  number: string;
  name: string;
  realm: string;
  sip: {
    username: string;
    password: string | null;
    server: string;
    port: number;
    transports: string[];
    uri: string;
  } | null;
  webrtc: {
    username: string;
    password: string | null;
    server: string;
    websocket: string;
    uri: string;
  } | null;
}

/**
 * Registration details for one extension, with the passwords.
 *
 * Fetched only when opened, and from an endpoint of its own rather than the extension row, so the
 * secrets never ride along with the list every other screen already loads.
 */
export function ExtensionCredentials({ id }: { id: string }) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['extension-credentials', id],
    queryFn: () => api.get<Credentials>(`/api/v1/extensions/${id}/credentials`),
    // Never cached beyond the moment it is on screen.
    gcTime: 0,
    staleTime: 0,
  });

  const rotate = useMutation({
    mutationFn: (kind: 'sip' | 'webrtc') =>
      api.post(`/api/v1/extensions/${id}/rotate-${kind}-secret`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['extension-credentials', id] }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading credentials…</p>;
  if (error) return <Alert>{error instanceof Error ? error.message : 'could not load'}</Alert>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {data.sip && (
        <Card className="space-y-1">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-medium">Desk phone / softphone</h3>
              <p className="text-sm text-muted-foreground">
                SIP over UDP or TCP on port {data.sip.port}.
              </p>
            </div>
            <Badge>SIP</Badge>
          </div>

          <Row label="SIP server" value={data.sip.server} />
          <Row label="Port" value={String(data.sip.port)} />
          <Row label="Transport" value={data.sip.transports.join(' or ')} copyable={false} />
          <Row label="Domain / realm" value={data.realm} />
          <Row label="Username" value={data.sip.username} />
          <Row label="Password" value={data.sip.password} secret />
          <Row label="SIP URI" value={data.sip.uri} />

          <RotateButton
            pending={rotate.isPending && rotate.variables === 'sip'}
            onClick={() => rotate.mutate('sip')}
            what="desk phone"
          />
        </Card>
      )}

      {data.webrtc && (
        <Card className="space-y-1">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-medium">Browser</h3>
              <p className="text-sm text-muted-foreground">
                A separate credential, so revoking browser access never disturbs a working desk
                phone.
              </p>
            </div>
            <Badge>WebRTC</Badge>
          </div>

          <Row label="WebSocket URL" value={data.webrtc.websocket} />
          <Row label="Domain / realm" value={data.realm} />
          <Row label="Username" value={data.webrtc.username} />
          <Row label="Password" value={data.webrtc.password} secret />
          <Row label="SIP URI" value={data.webrtc.uri} />

          <RotateButton
            pending={rotate.isPending && rotate.variables === 'webrtc'}
            onClick={() => rotate.mutate('webrtc')}
            what="browser"
          />
        </Card>
      )}
    </div>
  );
}

function RotateButton({
  pending,
  onClick,
  what,
}: {
  pending: boolean;
  onClick: () => void;
  what: string;
}) {
  return (
    <div className="flex items-center gap-3 pt-3">
      <Button variant="secondary" onClick={onClick} disabled={pending}>
        <RefreshCw size={14} className={cn(pending && 'animate-spin')} />
        Generate new password
      </Button>
      <span className="text-xs text-muted-foreground">
        Any {what} using the old password stops registering until it is updated.
      </span>
    </div>
  );
}

function Row({
  label,
  value,
  secret = false,
  copyable = true,
}: {
  label: string;
  value: string | null;
  secret?: boolean;
  copyable?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (value === null) {
    return (
      <div className="flex items-center gap-3 border-b border-border py-2 text-sm last:border-0">
        <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
        <span className="text-muted-foreground italic">not set</span>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access is denied outside a secure context; the value is selectable regardless.
    }
  };

  return (
    <div className="flex items-center gap-3 border-b border-border py-2 text-sm last:border-0">
      <span className="w-40 shrink-0 text-muted-foreground">{label}</span>

      <span className="min-w-0 flex-1 truncate font-mono text-[13px]">
        {secret && !revealed ? '••••••••••••' : value}
      </span>

      {secret && (
        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          className="shrink-0 rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted"
          aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
          title={revealed ? 'Hide' : 'Show'}
        >
          {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      )}

      {copyable && (
        <button
          type="button"
          onClick={() => void copy()}
          className="shrink-0 rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted"
          aria-label={`Copy ${label}`}
          title="Copy"
        >
          {copied ? <Check size={15} className="text-success" /> : <Copy size={15} />}
        </button>
      )}
    </div>
  );
}
